import { useEffect, useRef, useState } from "react";
import { ButtonSize, IconSize } from "../../../../classes/enums/ButtonEnum";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import IconButton from "../../../../components/iconButton/IconButton";
import "./digitalTwinTimeline.css";

// Each entry delegates selection up, using the parent-managed selectedTwinId
const TimeLineTwinEntry = ({
  twin,
  selectedTwinId,
  comparedTwinId,
  onSelectTwin,
  comparisonMode,
  twinValidationStatus = {},
}) => {
  const isSelected = selectedTwinId === twin.id || comparedTwinId === twin.id;
  const twinRef = useRef(null);

  // Determine which icon to use based on validation status
  // Default to DOLLFRONT during loading or if validation status is unavailable
  const iconToUse =
    twinValidationStatus[twin.id] === true
      ? IconsEnum.DOLLVERIFIED
      : IconsEnum.DOLLFRONT;

  return (
    <div
      className={`DigitalTwinTimeline-entry-marker ${
        isSelected ? "DigitalTwinTimeline-entry-marker-selected" : ""
      }`}
      ref={twinRef}
    >
      <div
        className={
          "DigitalTwinTimeline-entry-marker-head " +
          (isSelected ? "DigitalTwinTimeline-entry-marker-head-selected" : "")
        }
      ></div>
      <div className="DigitalTwinTimeline-entry-icon-row">
        <IconButton
          icon={iconToUse}
          size={ButtonSize.MEDIUM}
          iconSize={IconSize.XXLARGE}
          noBgColor
          isTogglable={true}
          isToggled={isSelected}
          onToggle={() =>
            onSelectTwin(
              isSelected && comparisonMode ? twin.id : isSelected ? null : twin.id
            )
          }
        />
      </div>
      <div
        className={`DigitalTwinTimeline-entry-year App-text-medium ${
          isSelected ? "DigitalTwinTimeline-entry-selected" : ""
        }`}
      >
        {twin.name}
      </div>
    </div>
  );
};

const TimeLineYearMarker = ({
  year,
  twins,
  selectedTwinId,
  comparedTwinId,
  onSelectTwin,
  comparisonMode,
  twinValidationStatus = {},
}) => {
  const yearNumber = parseInt(year, 10);
  const twinsArray = Object.values(twins || {})
    .filter(twin => Math.floor(twin.latestDate / 10000) === yearNumber)
    .sort((a, b) => b.latestDate - a.latestDate);

  return (
    <div className="DigitalTwinTimeline-year-block">
      <div className="DigitalTwinTimeline-year-marker">
        <div className="DigitalTwinTimeline-year-marker-text App-text-medium">
          {yearNumber}
        </div>
      </div>
      <div className="DigitalTwinTimeline-year-entries">
        {twinsArray.map(twin => (
          <TimeLineTwinEntry
            key={twin.id}
            twin={twin}
            selectedTwinId={selectedTwinId}
            comparedTwinId={comparedTwinId}
            onSelectTwin={onSelectTwin}
            comparisonMode={comparisonMode}
            twinValidationStatus={twinValidationStatus}
          />
        ))}
      </div>
    </div>
  );
};

// Controlled Timeline: selection driven by parent via selectedTwinId prop
const DigitalTwinTimeline = ({
  twins = {},
  selectedTwinId,
  comparedTwinId,
  onTwinSelect,
  comparisonMode,
  twinValidationStatus = {},
}) => {
  const [years, setYears] = useState([]);

  useEffect(() => {
    const uniqueYears = new Set();
    Object.values(twins).forEach(twin => {
      uniqueYears.add(Math.floor(twin.oldestDate / 10000));
      uniqueYears.add(Math.floor(twin.latestDate / 10000));
    });
    setYears([...uniqueYears].sort((a, b) => b - a));
  }, [twins]);

  return (
    <div className="DigitalTwinTimeline-container">
      <div className="DigitalTwinTimeline-header">
        <div className="DigitalTwinTimeline-title App-title-widgets">
          Digital Twins
        </div>
      </div>
      <div className="DigitalTwinTimeline-content">
        <div className="DigitalTwinTimeline-wrapper">
          <div className="DigitalTwinTimeline-line"></div>
          <div className="DigitalTwinTimeline-years">
            {years.map(year => (
              <TimeLineYearMarker
                key={year}
                year={year}
                twins={twins}
                selectedTwinId={selectedTwinId}
                comparedTwinId={comparedTwinId}
                onSelectTwin={onTwinSelect}
                comparisonMode={comparisonMode}
                twinValidationStatus={twinValidationStatus}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DigitalTwinTimeline;
